/**
 * The rundown list page (container): TanStack Query for the list and the
 * create mutation, the dialog's open state, and a toast on success. Rendering
 * is `RundownList` + `CreateRundownDialog`.
 */
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import { CreateRundownDialog } from '../components/CreateRundownDialog.js';
import { RundownList, rundownHref } from '../components/RundownList.js';
import { useCreateRundown, useRundowns } from '../app/hooks.js';
import { NodeSiteWithoutStudios } from './CloudOnlyPage.js';

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export function RundownsView() {
  const rundowns = useRundowns();
  const create = useCreateRundown();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="mx-auto grid max-w-5xl gap-6">
      {rundowns.data?.length === 0 ? <NodeSiteWithoutStudios what="rundowns" /> : null}
      <RundownList
        rundowns={rundowns.data ?? []}
        loading={rundowns.isFetching}
        error={rundowns.error === null ? null : messageOf(rundowns.error)}
        onCreate={() => {
          create.reset();
          setOpen(true);
        }}
        onRetry={() => {
          void rundowns.refetch();
        }}
        onOpen={(rundownId) => {
          void navigate({ to: rundownHref(rundownId) });
        }}
      />
      <CreateRundownDialog
        open={open}
        onOpenChange={setOpen}
        pending={create.isPending}
        error={create.error === null ? null : messageOf(create.error)}
        onSubmit={(name) => {
          create.mutate(name, {
            onSuccess: (created) => {
              setOpen(false);
              toast.success(`Created “${created.name}”`);
            },
          });
        }}
      />
    </div>
  );
}
